/**
 * adminService — thin wrappers over the shared axios client for the admin
 * management endpoints introduced for the admin module rework (active users,
 * groups, posts, reels). Existing admin calls elsewhere (UserManagement,
 * NewsManagement, …) already use `api` directly; this collects the new ones in
 * one place so the new pages have a single, typed entry point.
 */
import api from './api'

const unwrap = <T,>(p: Promise<{ data: { data: T } }>) => p.then((r) => r.data.data)

export interface ActiveUsersResponse {
  window: string
  since: string
  users: { content: AdminUserRow[]; totalElements: number; totalPages: number }
  activeNow: number
  activeToday: number
  activeThisWeek: number
  neverActive: number
}

export interface AdminUserRow {
  id: number
  email: string
  firstName: string
  lastName: string
  fullName: string
  username?: string
  role: string
  userType?: string
  status: string
  lastLogin: string | null
  lastActivity: string | null
  createdAt: string
}

// Active-users overview — device/browser/OS/IP breakdowns over the last 15 min
// (who is online right now, on what device). Mirrors the Strishakti superadmin
// active-users overview shape (byDevice/byBrowser/byOS + a location proxy).
export interface ActiveUsersOverview {
  activeNow: number
  activeToday: number
  activeThisWeek: number
  neverActive: number
  byDevice: Record<string, number>
  byBrowser: Record<string, number>
  byOs: Record<string, number>
  byIpAddress: { label: string; count: number }[]
}

// Reports (admin-global)
export interface ReportRow {
  id: number
  postId: number | null
  commentId: number | null
  reelId: number | null
  communityId: number | null
  reporter: { id: number; fullName: string; username?: string } | null
  reason: string
  details: string | null
  status: string
  resolvedBy: { id: number; fullName: string } | null
  resolutionNote: string | null
  createdAt: string
}

export interface ReportResolveBody {
  status: string
  resolutionNote?: string
}

// Group detail (admin view)
export interface GroupMember {
  id: number
  fullName: string
  email: string
  role: string
}

export interface AdminGroupRow {
  id: number
  name: string
  description?: string
  privacy: string
  category?: string
  memberCount: number
  postCount: number
  admin?: { id: number; firstName: string; lastName: string }
  createdAt: string
}

export interface AdminPostRow {
  id: number
  userId: number | null
  userName: string | null
  content: string | null
  postType: string
  visibility: string
  likeCount: number
  commentCount: number
  shareCount: number
  reshareCount: number
  viewCount: number
  isPinned: boolean
  isFeatured: boolean
  createdAt: string
}

export interface AdminReelRow {
  id: number
  userId: number | null
  userName: string | null
  caption: string | null
  videoUrl: string
  thumbnailUrl?: string
  visibility: string
  viewCount: number
  likeCount: number
  commentCount: number
  shareCount: number
  reshareCount: number
  createdAt: string
}

interface PageResult<T> { content: T[]; totalElements: number; totalPages: number }

const page = <T,>(r: { data: { data: PageResult<T> } }) => r.data.data

// Aggregate counts for the Post Manager / Reel Management overview cards.
// One shape serves both (mirrors backend ContentCounts record).
export interface ContentCounts {
  total: number
  totalViews: number
  totalLikes: number
  totalComments: number
  today: number
  avgViews: number
  publicCount: number
  friendsCount: number
  privateCount: number
}

export const adminService = {
  // Active users
  getActiveUsers: (window: string, pageN = 0, size = 20) =>
    api.get<{ data: ActiveUsersResponse }>(`/admin/active-users`, {
      params: { window, page: pageN, size },
    }).then((r) => r.data.data),

  // Active users overview — device/browser/OS/IP breakdowns.
  getActiveUsersOverview: () =>
    api.get<{ data: ActiveUsersOverview }>(`/admin/active-users/overview`).then((r) => r.data.data),

  // Groups
  getAdminGroups: (search: string, pageN = 0, size = 20) =>
    api.get(`/admin/groups`, { params: { search: search || undefined, page: pageN, size } }).then((r) => page<AdminGroupRow>(r as any)),
  deleteAdminGroup: (id: number) => api.delete(`/admin/groups/${id}`).then((r) => r.data),
  getAdminGroup: (id: number) =>
    api.get(`/admin/groups/${id}`).then((r) => r.data.data),
  getAdminGroupMembers: (id: number) =>
    api.get(`/admin/groups/${id}/members`).then((r) => r.data.data as GroupMember[]),

  // Posts
  getAdminPosts: (search: string, pageN = 0, size = 20) =>
    api.get(`/admin/posts`, { params: { search: search || undefined, page: pageN, size } }).then((r) => page<AdminPostRow>(r as any)),
  getAdminPostCounts: () =>
    api.get<{ data: ContentCounts }>(`/admin/posts/counts`).then((r) => r.data.data),
  deleteAdminPost: (id: number) => api.delete(`/admin/posts/${id}`).then((r) => r.data),

  // Reels
  getAdminReels: (search: string, pageN = 0, size = 20) =>
    api.get(`/admin/reels`, { params: { search: search || undefined, page: pageN, size } }).then((r) => page<AdminReelRow>(r as any)),
  getAdminReelCounts: () =>
    api.get<{ data: ContentCounts }>(`/admin/reels/counts`).then((r) => r.data.data),
  deleteAdminReel: (id: number) => api.delete(`/admin/reels/${id}`).then((r) => r.data),
  toggleReelVisibility: (id: number) =>
    api.post(`/admin/reels/${id}/toggle-visibility`).then((r) => r.data.data as AdminReelRow),

  // Reports (admin-global)
  getAdminReports: (status: string, reason: string, pageN = 0, size = 20) =>
    api.get(`/admin/reports`, {
      params: { status: status || undefined, reason: reason || undefined, page: pageN, size },
    }).then((r) => page<ReportRow>(r as any)),
  getAdminReportCounts: () =>
    api.get<{ data: Record<string, number> }>(`/admin/reports/counts`).then((r) => r.data.data),
  resolveReport: (id: number, body: ReportResolveBody) =>
    api.put(`/admin/reports/${id}/resolve`, body).then((r) => r.data.data as ReportRow),
}